import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Check, Plus, CreditCard, Loader, X, Mail, User, Calendar, Clock, MapPin, Users, MessageSquare, Phone, ChevronLeft, ChevronRight, Tag } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';
import { useSEO } from '../hooks/useSEO';
import { getImageUrl } from '../utils/imageUrl';
import './Packages.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Location options for Jamaica
const LOCATIONS = [
  { value: '', label: 'Select a location' },
  { value: 'Montego Bay - Tropical Bliss Beach', label: 'Montego Bay - Tropical Bliss Beach' },
  { value: 'Kingston - Portmore Harbor Beach', label: 'Kingston - Portmore Harbor Beach' },
  { value: 'Negril - Jamwest Beach', label: 'Negril - Jamwest Beach' },
  { value: 'Negril - Long Bay Beach', label: 'Negril - Long Bay Beach' },
  { value: 'Other', label: 'Other Location (specify in requests)' }
];

// Time slots for booking
const TIME_SLOTS = [
  { value: '', label: 'Select a time' },
  { value: '5:00 PM', label: '5:00 PM - Sunset' },
  { value: '5:30 PM', label: '5:30 PM' },
  { value: '6:00 PM', label: '6:00 PM' },
  { value: '6:30 PM', label: '6:30 PM' },
  { value: '7:00 PM', label: '7:00 PM' },
  { value: '7:30 PM', label: '7:30 PM' },
  { value: '8:00 PM', label: '8:00 PM' }
];


const PackageGallery = ({ images, name }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goTo = (idx) => setCurrentIndex(idx);
  const goPrev = () => setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  const goNext = () => setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  return (
    <div className="pkg-gallery" data-testid="package-image-gallery">
      <img src={getImageUrl(images[currentIndex])} alt={`${name} - ${currentIndex + 1}`} className="package-detail-image" />
      <button className="gallery-nav gallery-nav-prev" onClick={goPrev} data-testid="gallery-prev-btn" aria-label="Previous image">
        <ChevronLeft size={20} />
      </button>
      <button className="gallery-nav gallery-nav-next" onClick={goNext} data-testid="gallery-next-btn" aria-label="Next image">
        <ChevronRight size={20} />
      </button>
      <div className="gallery-dots" data-testid="gallery-dots">
        {images.map((_, idx) => (
          <button
            key={idx}
            className={`gallery-dot ${idx === currentIndex ? 'active' : ''}`}
            onClick={() => goTo(idx)}
            aria-label={`Go to image ${idx + 1}`}
          />
        ))}
      </div>
      <span className="gallery-counter">{currentIndex + 1} / {images.length}</span>
    </div>
  );
};

const Packages = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useSEO('/packages');
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const [capturingPayment, setCapturingPayment] = useState(false);
  const bookingFormRef = useRef(null);
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    guestCount: 2,
    eventDate: '',
    eventTime: '',
    location: '',
    specialRequests: ''
  });

  const [blockedDates, setBlockedDates] = useState([]);
  const [dateError, setDateError] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState([]);

  useEffect(() => {
    fetchPackages();
    fetchBlockedDates();
    fetchPaypalClientId();
  }, []);

  // Scroll to specific package if hash is present
  useEffect(() => {
    if (!loading && location.hash) {
      const el = document.getElementById(location.hash.replace('#', ''));
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.style.transition = 'box-shadow 0.3s ease';
          el.style.boxShadow = '0 0 0 3px #5f9dff';
          setTimeout(() => { el.style.boxShadow = 'none'; }, 2000);
        }, 300);
      }
    }
  }, [loading, location.hash]);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data.packages || []);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedDates = async () => {
    try {
      // Get blocked dates for the next 6 months
      const today = new Date();
      const startDate = today.toISOString().split('T')[0];
      const endDate = new Date(today.setMonth(today.getMonth() + 6)).toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/availability/blocked-dates?start_date=${startDate}&end_date=${endDate}`);
      setBlockedDates(response.data.blocked_dates || []);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  };

  const fetchPaypalClientId = async () => {
    try {
      const response = await axios.get(`${API}/payments/paypal/client-id`);
      setPaypalClientId(response.data.client_id);
      setPaypalReady(true);
    } catch (error) {
      console.error('PayPal not configured:', error);
      setPaypalReady(false);
    }
  };

  const openBookingModal = (pkg) => {
    setSelectedPackage(pkg);
    setBookingForm({
      name: '',
      email: '',
      phone: '',
      guestCount: pkg.base_guests || 2,
      eventDate: '',
      eventTime: '',
      location: '',
      specialRequests: ''
    });
    setDateError('');
    setPromoCode('');
    setPromoDiscount(null);
    setPromoError('');
    setSelectedAddOns([]);
    setShowBookingModal(true);
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setSelectedPackage(null);
    setDateError('');
    setPromoCode('');
    setPromoDiscount(null);
    setPromoError('');
    setSelectedAddOns([]);
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    setBookingForm({ ...bookingForm, eventDate: selectedDate });
    
    if (blockedDates.includes(selectedDate)) {
      setDateError('This date is not available. Please select another date.');
    } else {
      setDateError('');
    }
  };

  // Calculate total price with extra guests, add-ons, and promo
  const calculatedTotal = useMemo(() => {
    if (!selectedPackage) return { basePrice: 0, extraCharges: 0, total: 0, extraGuests: 0, addOnsTotal: 0 };
    
    const basePrice = selectedPackage.price || 0;
    const baseGuests = selectedPackage.base_guests || 2;
    const extraPersonCost = selectedPackage.extra_person_cost || 0;
    const guestCount = bookingForm.guestCount || baseGuests;
    
    const extraGuests = Math.max(0, guestCount - baseGuests);
    const extraCharges = extraGuests * extraPersonCost;

    const addOnsTotal = selectedAddOns.reduce((sum, addon) => sum + (addon.price || 0) * (addon.qty || 1), 0);

    const subtotal = basePrice + extraCharges + addOnsTotal;

    const discountAmount = promoDiscount ? Math.round(subtotal * promoDiscount / 100 * 100) / 100 : 0;
    const total = Math.max(0.50, subtotal - discountAmount);
    
    return { basePrice, extraCharges, total, extraGuests, extraPersonCost, subtotal, discountAmount, addOnsTotal };
  }, [selectedPackage, bookingForm.guestCount, promoDiscount, selectedAddOns]);

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    setPromoDiscount(null);
    try {
      const res = await axios.post(`${API}/promo-codes/validate`, { code: promoCode });
      setPromoDiscount(res.data.discount_percent);
    } catch (err) {
      setPromoError(err.response?.data?.detail || 'Invalid promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  // Validate the booking form before PayPal flow starts
  const validateBookingForm = useCallback(() => {
    if (!bookingForm.name.trim()) return 'Please enter your name';
    if (!bookingForm.email.trim()) return 'Please enter your email';
    if (!bookingForm.eventDate) return 'Please select a date';
    if (blockedDates.includes(bookingForm.eventDate)) return 'Selected date is not available';
    if (!bookingForm.eventTime) return 'Please select a time';
    if (!bookingForm.location) return 'Please select a location';
    return null;
  }, [bookingForm, blockedDates]);

  // Use refs so PayPal callbacks always have latest values without causing re-renders
  const formRef = useRef(bookingForm);
  const packageRef = useRef(selectedPackage);
  const promoRef = useRef({ promoCode, promoDiscount });
  const addOnsRef = useRef(selectedAddOns);
  
  useEffect(() => { formRef.current = bookingForm; }, [bookingForm]);
  useEffect(() => { packageRef.current = selectedPackage; }, [selectedPackage]);
  useEffect(() => { promoRef.current = { promoCode, promoDiscount }; }, [promoCode, promoDiscount]);
  useEffect(() => { addOnsRef.current = selectedAddOns; }, [selectedAddOns]);

  // PayPal createOrder handler - must NOT trigger state updates (re-renders kill the popup)
  const handlePayPalCreateOrder = useCallback(async () => {
    const form = formRef.current;
    const pkg = packageRef.current;
    const promo = promoRef.current;
    const addOns = addOnsRef.current;

    // Validate
    if (!form.name.trim()) { alert('Please enter your name'); throw new Error('validation'); }
    if (!form.email.trim()) { alert('Please enter your email'); throw new Error('validation'); }
    if (!form.eventDate) { alert('Please select a date'); throw new Error('validation'); }
    if (!form.eventTime) { alert('Please select a time'); throw new Error('validation'); }
    if (!form.location) { alert('Please select a location'); throw new Error('validation'); }

    const response = await axios.post(`${API}/payments/paypal/create-order`, {
      package_id: pkg.id,
      customer_email: form.email,
      customer_name: form.name,
      customer_phone: form.phone,
      guest_count: parseInt(form.guestCount),
      event_date: form.eventDate,
      event_time: form.eventTime,
      location: form.location,
      special_requests: form.specialRequests,
      promo_code: promo.promoDiscount ? promo.promoCode : null,
      selected_add_ons: addOns
    });

    return response.data.paypal_order_id;
  }, []);

  // PayPal onApprove handler - called after customer approves payment
  const handlePayPalApprove = useCallback(async (data) => {
    try {
      setCapturingPayment(true);
      const response = await axios.post(`${API}/payments/paypal/capture-order/${data.orderID}`);
      
      if (response.data.status === 'COMPLETED') {
        setShowBookingModal(false);
        setSelectedPackage(null);
        navigate(`/payment/success?booking_id=${response.data.booking_id}`);
      } else {
        alert('Payment was not completed. Please try again.');
        setCapturingPayment(false);
      }
    } catch (error) {
      console.error('Error capturing payment:', error);
      alert('Payment processing failed. Please try again or contact us.');
      setCapturingPayment(false);
    }
  }, [navigate]);

  const handlePayPalError = useCallback((err) => {
    console.error('PayPal error:', err);
    setCapturingPayment(false);
    alert('Payment could not be processed. Please try again.');
  }, []);

  const handlePayPalCancel = useCallback(() => {
    setCapturingPayment(false);
  }, []);

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (loading) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading packages...</div>;
  }

  return (
    <div className="packages">
      {/* Hero Section */}
      <section className="packages-hero">
        <div className="container">
          <h1 className="hero-medium">Our Packages</h1>
          <p className="body-large packages-subtitle">
            Curated experiences designed for life's most special moments
          </p>
        </div>
      </section>

      {/* Packages List */}
      <section className="packages-list section-padding">
        <div className="container">
          {packages.map((pkg, index) => (
            <div
              key={pkg.id}
              id={`pkg-${pkg.id}`}
              className={`package-detail ${index % 2 === 1 ? 'reverse' : ''}`}
            >
              <div className="package-detail-image-wrapper" data-testid={`package-gallery-${pkg.id}`}>
                {(() => {
                  const allImages = [pkg.image, ...(pkg.images || [])].filter(Boolean);
                  if (allImages.length <= 1) {
                    return (
                      <img
                        src={getImageUrl(pkg.image)}
                        alt={pkg.name}
                        className="package-detail-image"
                      />
                    );
                  }
                  return <PackageGallery images={allImages.map(getImageUrl)} name={pkg.name} />;
                })()}
              </div>
              <div className="package-detail-content">
                <h2 className="heading-1">{pkg.name}</h2>
                <div className="package-pricing-info">
                  <p className="body-large package-detail-price">
                    ${typeof pkg.price === 'number' ? pkg.price.toFixed(2) : pkg.price}
                  </p>
                  <p className="package-guests-included">
                    for {pkg.base_guests || 2} guests
                  </p>
                  {pkg.extra_person_cost > 0 && (
                    <p className="package-extra-cost">
                      +${pkg.extra_person_cost.toFixed(2)} per extra person
                    </p>
                  )}
                </div>
                <p className="body-regular package-detail-description">
                  {pkg.description}
                </p>

                <div className="package-inclusions">
                  <h3 className="heading-3">Included in this package:</h3>
                  <ul className="inclusions-list">
                    {pkg.inclusions && pkg.inclusions.length > 0 ? (
                      pkg.inclusions.map((item, idx) => (
                        <li key={idx} className="body-regular">
                          <Check size={18} />
                          {item}
                        </li>
                      ))
                    ) : (
                      <li className="body-regular">No inclusions listed</li>
                    )}
                  </ul>
                </div>

                <div className="package-addons">
                  <h3 className="heading-3">Available add-ons:</h3>
                  <ul className="addons-list">
                    {pkg.add_ons && pkg.add_ons.length > 0 ? (
                      pkg.add_ons.map((addon, idx) => {
                        const addonName = typeof addon === 'string' ? addon : addon.name;
                        const addonPrice = typeof addon === 'object' && addon.price ? addon.price : null;
                        
                        return (
                          <li key={idx} className="body-regular">
                            <Plus size={18} />
                            <span>
                              {addonName}
                              {addonPrice && (
                                <span className="addon-price"> (+${addonPrice.toFixed(2)})</span>
                              )}
                            </span>
                          </li>
                        );
                      })
                    ) : (
                      <li className="body-regular">No add-ons available</li>
                    )}
                  </ul>
                </div>

                <div className="package-action-buttons">
                  <button 
                    onClick={() => openBookingModal(pkg)} 
                    className="btn-primary btn-book-now"
                    disabled={processingPayment === pkg.id}
                    data-testid={`book-now-${pkg.id}`}
                  >
                    {processingPayment === pkg.id ? (
                      <>
                        <Loader size={18} className="spinner" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard size={18} />
                        Book & Pay Now
                      </>
                    )}
                  </button>
                  <Link to="/contact" className="btn-secondary btn-inquire">
                    Inquire First
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Customization CTA */}
      <section className="customization-cta section-padding-small">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-1">Need Something Different?</h2>
            <p className="body-large">
              Every celebration is unique. Let us create a completely custom experience 
              tailored to your specific vision and requirements.
            </p>
            <Link to="/contact" className="btn-primary">
              Request Custom Package
            </Link>
          </div>
        </div>
      </section>

      {/* Enhanced Booking Modal */}
      {showBookingModal && selectedPackage && (
        <div className="booking-modal-overlay" onClick={closeBookingModal}>
          <div className="booking-modal booking-modal-enhanced" onClick={(e) => e.stopPropagation()}>
            <button className="booking-modal-close" onClick={closeBookingModal}>
              <X size={24} />
            </button>
            
            <div className="booking-modal-header">
              <h3>Customize Your Experience</h3>
              <p className="booking-modal-package">{selectedPackage.name}</p>
            </div>
            
            <form ref={bookingFormRef} className="booking-modal-form" onSubmit={(e) => e.preventDefault()}>
              {/* Contact Information */}
              <div className="booking-section">
                <h4 className="booking-section-title">Contact Information</h4>
                
                <div className="booking-form-row">
                  <div className="booking-form-group">
                    <label>
                      <User size={16} />
                      Your Name *
                    </label>
                    <input
                      type="text"
                      value={bookingForm.name}
                      onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                      placeholder="Enter your full name"
                      required
                      data-testid="booking-name-input"
                    />
                  </div>
                  
                  <div className="booking-form-group">
                    <label>
                      <Phone size={16} />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={bookingForm.phone}
                      onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                      data-testid="booking-phone-input"
                    />
                  </div>
                </div>
                
                <div className="booking-form-group">
                  <label>
                    <Mail size={16} />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={bookingForm.email}
                    onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                    placeholder="Enter your email for confirmation"
                    required
                    data-testid="booking-email-input"
                  />
                </div>
              </div>

              {/* Event Details */}
              <div className="booking-section">
                <h4 className="booking-section-title">Event Details</h4>
                
                <div className="booking-form-row">
                  <div className="booking-form-group">
                    <label>
                      <Calendar size={16} />
                      Preferred Date *
                    </label>
                    <input
                      type="date"
                      value={bookingForm.eventDate}
                      onChange={handleDateChange}
                      min={getMinDate()}
                      required
                      className={dateError ? 'has-error' : ''}
                      data-testid="booking-date-input"
                    />
                    {dateError && (
                      <span className="date-error">{dateError}</span>
                    )}
                  </div>
                  
                  <div className="booking-form-group">
                    <label>
                      <Clock size={16} />
                      Preferred Time *
                    </label>
                    <select
                      value={bookingForm.eventTime}
                      onChange={(e) => setBookingForm({ ...bookingForm, eventTime: e.target.value })}
                      required
                      data-testid="booking-time-input"
                    >
                      {TIME_SLOTS.map(slot => (
                        <option key={slot.value} value={slot.value}>{slot.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="booking-form-group">
                  <label>
                    <MapPin size={16} />
                    Location *
                  </label>
                  <select
                    value={bookingForm.location}
                    onChange={(e) => setBookingForm({ ...bookingForm, location: e.target.value })}
                    required
                    data-testid="booking-location-input"
                  >
                    {LOCATIONS.map(loc => (
                      <option key={loc.value} value={loc.value}>{loc.label}</option>
                    ))}
                  </select>
                </div>
                
                <div className="booking-form-group">
                  <label>
                    <Users size={16} />
                    Number of Guests *
                  </label>
                  <div className="guest-counter">
                    <button
                      type="button"
                      className="guest-counter-btn"
                      onClick={() => setBookingForm({ 
                        ...bookingForm, 
                        guestCount: Math.max(1, bookingForm.guestCount - 1) 
                      })}
                      data-testid="guest-decrease-btn"
                    >
                      -
                    </button>
                    <span className="guest-count" data-testid="guest-count-display">
                      {bookingForm.guestCount}
                    </span>
                    <button
                      type="button"
                      className="guest-counter-btn"
                      onClick={() => setBookingForm({ 
                        ...bookingForm, 
                        guestCount: Math.min(selectedPackage.max_guests || 20, bookingForm.guestCount + 1) 
                      })}
                      data-testid="guest-increase-btn"
                    >
                      +
                    </button>
                  </div>
                  <p className="guest-note">
                    Base price includes {selectedPackage.base_guests || 2} guests
                    {selectedPackage.extra_person_cost > 0 && (
                      <> • Extra guests: +${selectedPackage.extra_person_cost.toFixed(2)} each</>
                    )}
                  </p>
                </div>
              </div>

              {/* Add-Ons Selection */}
              {selectedPackage.add_ons && selectedPackage.add_ons.length > 0 && (
                <div className="booking-section">
                  <h4 className="booking-section-title">Add-Ons</h4>
                  <p style={{ fontSize: '13px', color: '#736c64', marginBottom: '12px' }}>
                    Enhance your experience with these extras
                  </p>
                  <div className="addon-selection-list" data-testid="addon-selection-list">
                    {selectedPackage.add_ons.map((addon, idx) => {
                      const selected = selectedAddOns.find(a => a.name === addon.name);
                      const isSelected = !!selected;
                      const qty = selected?.qty || 1;
                      return (
                        <div
                          key={idx}
                          className={`addon-selection-item ${isSelected ? 'selected' : ''}`}
                          data-testid={`addon-item-${idx}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              if (isSelected) {
                                setSelectedAddOns(prev => prev.filter(a => a.name !== addon.name));
                              } else {
                                setSelectedAddOns(prev => [...prev, { name: addon.name, price: addon.price || 0, qty: 1 }]);
                              }
                            }}
                            data-testid={`addon-checkbox-${idx}`}
                          />
                          <div className="addon-selection-info">
                            <span className="addon-selection-name">{addon.name}</span>
                            {isSelected && addon.price > 0 ? (
                              <div className="addon-qty-controls" data-testid={`addon-qty-${idx}`}>
                                <button
                                  type="button"
                                  className="addon-qty-btn"
                                  onClick={() => {
                                    if (qty <= 1) {
                                      setSelectedAddOns(prev => prev.filter(a => a.name !== addon.name));
                                    } else {
                                      setSelectedAddOns(prev => prev.map(a => a.name === addon.name ? { ...a, qty: a.qty - 1 } : a));
                                    }
                                  }}
                                  data-testid={`addon-minus-${idx}`}
                                >
                                  -
                                </button>
                                <span className="addon-qty-value">{qty}</span>
                                <button
                                  type="button"
                                  className="addon-qty-btn"
                                  onClick={() => {
                                    setSelectedAddOns(prev => prev.map(a => a.name === addon.name ? { ...a, qty: a.qty + 1 } : a));
                                  }}
                                  data-testid={`addon-plus-${idx}`}
                                >
                                  +
                                </button>
                                <span className="addon-selection-price">
                                  ${(addon.price * qty).toFixed(2)}
                                </span>
                              </div>
                            ) : (
                              <span className="addon-selection-price">
                                {addon.price > 0 ? `+$${addon.price.toFixed(2)}` : 'Free'}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Special Requests */}
              <div className="booking-section">
                <h4 className="booking-section-title">Special Requests</h4>
                
                <div className="booking-form-group">
                  <label>
                    <MessageSquare size={16} />
                    Dietary Requirements & Special Requests
                  </label>
                  <textarea
                    value={bookingForm.specialRequests}
                    onChange={(e) => setBookingForm({ ...bookingForm, specialRequests: e.target.value })}
                    placeholder="Any allergies, dietary restrictions, special occasions, decoration preferences, or other requests..."
                    rows={4}
                    data-testid="booking-requests-input"
                  />
                </div>
              </div>

              {/* Promo Code */}
              <div className="booking-section">
                <h4 className="booking-section-title">Promo Code</h4>
                <div className="booking-form-group">
                  <label>
                    <Tag size={16} />
                    Have a promo code?
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value.toUpperCase());
                        if (promoDiscount) { setPromoDiscount(null); setPromoError(''); }
                      }}
                      placeholder="Enter code"
                      style={{ flex: 1, textTransform: 'uppercase', letterSpacing: '2px', fontFamily: 'monospace' }}
                      data-testid="booking-promo-input"
                    />
                    <button
                      type="button"
                      onClick={handleApplyPromo}
                      disabled={promoLoading || !promoCode.trim()}
                      style={{
                        padding: '10px 20px',
                        background: promoDiscount ? '#16a34a' : '#1a1918',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontWeight: 600,
                        cursor: promoLoading || !promoCode.trim() ? 'not-allowed' : 'pointer',
                        opacity: promoLoading || !promoCode.trim() ? 0.5 : 1,
                        fontSize: '14px',
                        whiteSpace: 'nowrap'
                      }}
                      data-testid="booking-promo-apply-btn"
                    >
                      {promoLoading ? '...' : promoDiscount ? 'Applied!' : 'Apply'}
                    </button>
                  </div>
                  {promoError && (
                    <span style={{ color: '#dc2626', fontSize: '13px', marginTop: '6px', display: 'block' }} data-testid="promo-error">{promoError}</span>
                  )}
                  {promoDiscount && (
                    <span style={{ color: '#16a34a', fontSize: '13px', marginTop: '6px', display: 'block', fontWeight: 600 }} data-testid="promo-success">
                      {promoDiscount}% discount applied!
                    </span>
                  )}
                </div>
              </div>

              {/* Price Summary */}
              <div className="booking-price-summary">
                <div className="price-row">
                  <span>Base Package ({selectedPackage.base_guests || 2} guests)</span>
                  <span>${calculatedTotal.basePrice.toFixed(2)}</span>
                </div>
                {calculatedTotal.extraGuests > 0 && (
                  <div className="price-row extra">
                    <span>Extra Guests ({calculatedTotal.extraGuests} x ${calculatedTotal.extraPersonCost.toFixed(2)})</span>
                    <span>+${calculatedTotal.extraCharges.toFixed(2)}</span>
                  </div>
                )}
                {selectedAddOns.map((addon, idx) => (
                  <div className="price-row extra" key={idx}>
                    <span>{addon.name}{addon.qty > 1 ? ` x${addon.qty}` : ''}</span>
                    <span>{addon.price > 0 ? `+$${(addon.price * (addon.qty || 1)).toFixed(2)}` : 'Free'}</span>
                  </div>
                ))}
                {calculatedTotal.discountAmount > 0 && (
                  <div className="price-row" style={{ color: '#16a34a' }}>
                    <span>Promo Code ({promoCode}) -{promoDiscount}%</span>
                    <span>-${calculatedTotal.discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="price-row total">
                  <span>Total</span>
                  <span>${calculatedTotal.total.toFixed(2)}</span>
                </div>
              </div>
              
              <p className="booking-modal-note">
                You'll receive a confirmation email after your payment is complete. Our team will contact you to finalize the details.
              </p>
              
              {/* PayPal Payment Buttons */}
              {capturingPayment ? (
                <div className="paypal-loading" data-testid="paypal-capturing">
                  <Loader size={24} className="spinner" />
                  <span>Processing your payment...</span>
                </div>
              ) : paypalReady && paypalClientId ? (
                <div className="paypal-buttons-container" data-testid="paypal-buttons-container">
                  <PayPalScriptProvider options={{ 
                    clientId: paypalClientId, 
                    currency: 'USD',
                    intent: 'capture',
                    components: 'buttons,card-fields'
                  }}>
                    <PayPalButtons
                      style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay', height: 48 }}
                      disabled={!!dateError}
                      forceReRender={[calculatedTotal.total]}
                      createOrder={handlePayPalCreateOrder}
                      onApprove={handlePayPalApprove}
                      onError={handlePayPalError}
                      onCancel={handlePayPalCancel}
                    />
                  </PayPalScriptProvider>
                  <p className="paypal-privacy-notice" style={{ fontSize: '11px', color: '#736c64', textAlign: 'center', marginTop: '8px' }}>
                    By paying with your card, you acknowledge that your data will be processed by PayPal subject to the <a href="https://www.paypal.com/privacy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>PayPal Privacy Statement</a>.
                  </p>
                  <p className="paypal-total-label">Total: ${calculatedTotal.total.toFixed(2)} USD</p>
                </div>
              ) : (
                <div className="paypal-not-configured" data-testid="paypal-not-configured">
                  <p>Online payment is currently being set up. Please <Link to="/contact">contact us</Link> to complete your booking.</p>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Packages;
